import { arrayOf, string, shape } from 'prop-types';
import React, { Fragment } from 'react';
import Head from 'next/head';
import {
  PERMUTIVE_API_KEY,
  PERMUTIVE_NAMESPACE
} from 'constants/ads/permutive';
import { GA_ID, FACEBOOK_PIXEL_ID, PARSELY_SITE } from 'constants/vendor';
import { getGaPageType, preparePostBundleItemTypes } from 'util/ga';
import { extractGtmMetadataFromJsonLd } from 'util/gtm';
import { extractMetadataFromBundleItems } from 'util/metadata';

const FB_PIXEL_SCRIPT = `
{
  "vars": {
    "pixelId": ${FACEBOOK_PIXEL_ID}
  },
  "triggers": {
    "trackPageview": {
      "on": "visible",
      "request": "pageview"
    }
  }
}`;

const PARSELY_SCRIPT = `
{
  "vars": {
    "apikey": "${PARSELY_SITE}"
  }
}`;

export const getGAConfig = (
  metadataItems,
  pageType,
  triggers,
  contentStats
) => {
  const parsed = extractMetadataFromBundleItems(metadataItems);
  const md = extractGtmMetadataFromJsonLd(parsed.jsonLd);

  const postBundleItemTypes =
    contentStats && contentStats.post && contentStats.post.bundleItemTypes
      ? preparePostBundleItemTypes(contentStats.post.bundleItemTypes)
      : null;

  return JSON.stringify({
    vars: {
      account: GA_ID,
      // we don't want the site suffix in the page title sent to GA
      title: parsed.title
    },
    extraUrlParams: {
      cd1: md.author || null,
      cd2: (md.categories && md.categories.join(', ')) || null,
      cd3: md.datePublished || null,
      cd26: md.dateModified || null,
      cd42: md.dateRepromoted || null,
      cd43: md.dateUpdated || null,
      // tags are joined with spaces to match GTM config
      cd7: (md.tags && md.tags.join(', ')) || null,
      cd17: (md.knowledgeTags && md.knowledgeTags.join(', ')) || null,
      cd11: 'gen-amp',
      // template is parsed client-side by AMP JS
      // eslint-disable-next-line no-template-curly-in-string
      cd45: '${ampdocHost}',
      cd47: parsed.recipeTaxonomy || null,
      cd48: (md.teams && md.teams.join(', ')) || null,
      cd51: (md.platforms && md.platforms.join(', ')) || null,

      // mirrors the logic in the "Post Bundle Item Types - String" GTM var
      cd52: postBundleItemTypes ? JSON.stringify(postBundleItemTypes) : null,
      cd54:
        (md.businessConversions && md.businessConversions.join(', ')) || null,
      cg1: getGaPageType(pageType) || null
    },
    triggers: {
      trackPageview: {
        on: 'visible',
        request: 'pageview'
      },
      ...triggers
    }
  });
};

export const getPermutiveConfig = (contentStats, metadataItems, pageType) => {
  const { article, recipe, products } =
    contentStats && contentStats.post ? contentStats.post : {};

  // Convert metadata and content stats into formatted payload
  const payload = {
    'properties.type': pageType,
    ...(article
      ? {
          'properties.article.authors!list[string]': article.authors,
          'properties.article.categories!list[string]': article.categories,
          'properties.article.tags!list[string]': article.tags,
          'properties.article.dateModified': article.dateModified,
          'properties.article.datePublished': article.datePublished,
          'properties.article.dateRepromoted': article.dateRepromoted,
          'properties.article.isSponsored': article.isSponsored,
          'properties.article.title': article.title
        }
      : undefined),
    ...(products
      ? {
          'properties.products.maxPrice': products.maxPrice,
          'properties.products.minPrice': products.minPrice,
          'properties.products.names!list[string]': products.names,
          'properties.products.retailers!list[string]': products.retailers,
          'properties.products.tags!list[string]': products.tags
        }
      : undefined),
    ...(recipe
      ? {
          ...(recipe.cookTime
            ? {
                'properties.recipe.cookTime.max': recipe.cookTime.max,
                'properties.recipe.cookTime.min': recipe.cookTime.min
              }
            : undefined),
          ...(recipe.prepTime
            ? {
                'properties.recipe.prepTime.max': recipe.prepTime.max,
                'properties.recipe.prepTime.min': recipe.prepTime.min
              }
            : undefined),

          'properties.recipe.taxonomy!list[string]': metadataItems.recipeTaxonomy
            ? metadataItems.recipeTaxonomy.split(',').map(s => s.trim())
            : undefined
        }
      : undefined)
  };

  return JSON.stringify({
    vars: {
      namespace: PERMUTIVE_NAMESPACE,
      key: PERMUTIVE_API_KEY
    },
    extraUrlParams: payload
  });
};

function AmpAnalytics(props) {
  const { metadataItems, pageType, triggers, contentStats } = props;

  return (
    <Fragment>
      <Head>
        <script
          async
          key="amp-analytics"
          custom-element="amp-analytics"
          src="https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
        />
      </Head>

      {/* Permutive */}
      <amp-analytics type="permutive">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: getPermutiveConfig(contentStats, metadataItems, pageType)
          }}
        />
      </amp-analytics>

      {/* GTM */}
      {/* Loads Comscore and Quantcast Measure */}
      <amp-analytics
        config="https://www.googletagmanager.com/amp.json?id=GTM-N8BZ9N7&gtm.url=SOURCE_URL"
        data-credentials="include"
      />

      {/* Google Analytics */}
      {/*
        NOTE: It's possible to use GTM for GA and AMP vars to set dimensions.
        However, it is not possible to set "content groups" in the AMP GA tag.
      */}
      <amp-analytics type="googleanalytics" data-credentials="include">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: getGAConfig(metadataItems, pageType, triggers, contentStats)
          }}
        />
      </amp-analytics>

      {/* FB Pixel */}
      {FACEBOOK_PIXEL_ID && (
        <amp-analytics type="facebookpixel" id="facebook-pixel">
          <script
            type="application/json"
            dangerouslySetInnerHTML={{
              __html: FB_PIXEL_SCRIPT
            }}
          />
        </amp-analytics>
      )}

      {/* Parsely */}
      {/*
        NOTE: Parsely can be implemented using GTM however pings must be fired
        manually. While this is possible using a GTM timer, there appears to be
        no way to set the `inc` value - consequently the required `inc` value is
        empty when the heartbeat pixel is fired.
      */}
      <amp-analytics type="parsely">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: PARSELY_SCRIPT
          }}
        />
      </amp-analytics>
    </Fragment>
  );
}

AmpAnalytics.propTypes = {
  pageType: string.isRequired,
  metadataItems: arrayOf(
    shape({
      // TBD
    })
  ).isRequired,
  triggers: shape({
    // TBD
  }),
  contentStats: shape({
    // TBD
  })
};

AmpAnalytics.defaultProps = {
  triggers: {},
  contentStats: undefined
};

export default AmpAnalytics;
